import { useEffect, useRef } from "react";
import { createErpSocket } from "../utils/socketIo";
import { getErpAuthState } from "../erpClient";
import {
  hasWhatsAppPermission,
  WHATSAPP_PERMISSION_CODES,
} from "../utils/whatsappPermissions";

function resolveTenantId() {
  const user = getErpAuthState().user;
  const n = Number(user?.tenant_id ?? user?.tenantId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function matchesTenant(payload) {
  const tenantId = resolveTenantId();
  if (!tenantId) return true;
  const payloadTenant = Number(payload?.tenantId ?? payload?.tenant_id);
  if (!Number.isFinite(payloadTenant) || payloadTenant <= 0) return true;
  return payloadTenant === tenantId;
}

/**
 * Subscribe to WhatsApp Socket.IO events for the inbox.
 * Requires WHATSAPP_INBOX or umbrella WHATSAPP permission.
 */
export function useWhatsAppInboxSocket({
  enabled = true,
  onIncomingMessage,
  onMessageStatus,
  onConnectionChange,
}) {
  const handlersRef = useRef({
    onIncomingMessage,
    onMessageStatus,
    onConnectionChange,
  });

  handlersRef.current = {
    onIncomingMessage,
    onMessageStatus,
    onConnectionChange,
  };

  useEffect(() => {
    if (!enabled) return undefined;
    if (!hasWhatsAppPermission(WHATSAPP_PERMISSION_CODES.INBOX)) {
      return undefined;
    }

    const socket = createErpSocket();

    const emitConnected = (connected) => {
      handlersRef.current.onConnectionChange?.(connected);
    };

    const handleConnect = () => emitConnected(true);
    const handleDisconnect = () => emitConnected(false);

    const handleIncoming = (payload) => {
      if (!matchesTenant(payload)) return;
      handlersRef.current.onIncomingMessage?.(payload);
    };

    const handleStatus = (payload) => {
      if (!matchesTenant(payload)) return;
      handlersRef.current.onMessageStatus?.(payload);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("whatsapp_incoming_message", handleIncoming);
    socket.on("whatsapp_message_status", handleStatus);

    if (socket.connected) {
      emitConnected(true);
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("whatsapp_incoming_message", handleIncoming);
      socket.off("whatsapp_message_status", handleStatus);
      socket.disconnect();
      emitConnected(false);
    };
  }, [enabled]);
}
